import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type CreditProgressBarProps } from "@src/appV2/redesign/EmergencyCredits/components/CreditProgressBar";
import { ShieldIcon } from "@src/appV2/redesign/EmergencyCredits/components/ShieldIcon";
import { createElement, type ReactNode, useCallback, useEffect, useState } from "react";

import { type ScoreDisplayProps } from "../components/ScoreDisplay";
import { ANIMATION_TIMINGS } from "../constants";
import { type ScoreChange } from "../types";
import { useCreditApplicationAnimation } from "./useCreditApplicationAnimation";
import { useScoreAnimationSequence } from "./useScoreAnimationSequence";

type CreditState = "idle" | "applying" | "applied";

interface CreditLineItemData {
  label: string;
  value: number;
  color: "positive";
  icon: ReactNode;
  iconPosition: "left" | "right";
  isVisible: boolean;
}

interface UseClipboardScorePageStateParams {
  startScore: number;
  scoreChanges: ScoreChange[];
  creditAmount: number;
  canUseCredit: boolean;
  autoApplyCredit: boolean;
  creditProgressBarProps?: Omit<CreditProgressBarProps, "showTooltip">;
  isButtonDisabled: boolean;
  onContinue: () => void;
}

interface UseClipboardScorePageStateResult {
  visibleLineItemCount: number;
  scoreDisplayProps: ScoreDisplayProps;
  effectiveCreditProgressBarProps?: Omit<CreditProgressBarProps, "showTooltip">;
  creditLineItem?: CreditLineItemData;
  creditState: CreditState;
  buttonText: string;
  buttonEnabled: boolean;
  handleButtonClick: () => void;
}

export function useClipboardScorePageState(
  params: UseClipboardScorePageStateParams
): UseClipboardScorePageStateResult {
  const {
    startScore,
    scoreChanges,
    creditAmount,
    canUseCredit,
    autoApplyCredit,
    creditProgressBarProps,
    isButtonDisabled,
    onContinue,
  } = params;

  const [creditState, setCreditState] = useState<CreditState>("idle");

  const {
    currentStartScore,
    currentEndScore,
    isScoreAnimating,
    visibleLineItemCount,
    isButtonEnabled,
  } = useScoreAnimationSequence(startScore, scoreChanges);

  const isAutoApplyPending = autoApplyCredit && canUseCredit && creditState === "idle";

  useEffect(() => {
    if (!isAutoApplyPending || !isButtonEnabled) {
      return;
    }

    const timer = setTimeout(() => {
      setCreditState("applying");
    }, ANIMATION_TIMINGS.BETWEEN_CHANGES_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [isAutoApplyPending, isButtonEnabled]);

  const handleCreditAnimationComplete = useCallback((): void => {
    setCreditState("applied");
  }, []);

  const { creditEndScore, isCreditScoreAnimating, isCreditLineItemVisible, progressBarLabel } =
    useCreditApplicationAnimation({
      creditAmount,
      baseScore: currentEndScore,
      isTriggered: creditState !== "idle",
      onComplete: handleCreditAnimationComplete,
    });

  const scoreDisplayProps = getScoreDisplayProps({
    creditState,
    currentStartScore,
    currentEndScore,
    isScoreAnimating,
    creditEndScore,
    isCreditScoreAnimating,
  });

  const effectiveCreditProgressBarProps = getEffectiveCreditProgressBarProps({
    creditState,
    creditProgressBarProps,
    progressBarLabel,
  });

  const creditLineItem = getCreditLineItem({
    canUseCredit,
    creditAmount,
    isCreditLineItemVisible,
  });

  const handleButtonClick = useCallback((): void => {
    if (canUseCredit && creditState === "idle") {
      logEvent(APP_V2_USER_EVENTS.YOUR_CLIPBOARD_SCORE_APPLY_CREDIT_CLICKED, {
        startScore,
        finalScore: currentEndScore,
        creditAmount,
      });
      setCreditState("applying");
    } else {
      logEvent(APP_V2_USER_EVENTS.YOUR_CLIPBOARD_SCORE_CONTINUE_CLICKED, {
        startScore,
        finalScore: creditState === "idle" ? currentEndScore : creditEndScore,
      });
      onContinue();
    }
  }, [
    canUseCredit,
    creditState,
    onContinue,
    startScore,
    currentEndScore,
    creditAmount,
    creditEndScore,
  ]);

  const isApplyButton = canUseCredit && !autoApplyCredit && creditState === "idle";
  const buttonText = isApplyButton ? "Apply my lateness credit" : "Continue";

  const buttonEnabled = getButtonEnabled({
    isButtonDisabled: isButtonDisabled || isAutoApplyPending,
    creditState,
    isButtonEnabled,
  });

  return {
    visibleLineItemCount,
    scoreDisplayProps,
    effectiveCreditProgressBarProps,
    creditLineItem,
    creditState,
    buttonText,
    buttonEnabled,
    handleButtonClick,
  };
}

function getEffectiveCreditProgressBarProps(params: {
  creditState: CreditState;
  creditProgressBarProps?: Omit<CreditProgressBarProps, "showTooltip">;
  progressBarLabel: string;
}): Omit<CreditProgressBarProps, "showTooltip"> | undefined {
  const { creditState, creditProgressBarProps, progressBarLabel } = params;

  if (!isDefined(creditProgressBarProps)) {
    return undefined;
  }

  if (creditState === "idle") {
    return { ...creditProgressBarProps, mode: "static" as const };
  }

  return {
    ...creditProgressBarProps,
    completedCount: 0,
    label: progressBarLabel,
    mode: "drain",
    subtext: undefined,
  };
}

function getCreditLineItem(params: {
  canUseCredit: boolean;
  creditAmount: number;
  isCreditLineItemVisible: boolean;
}): CreditLineItemData | undefined {
  const { canUseCredit, creditAmount, isCreditLineItemVisible } = params;

  if (!canUseCredit || creditAmount === 0) {
    return undefined;
  }

  return {
    label: "Lateness Credit",
    value: creditAmount,
    color: "positive",
    icon: createElement(ShieldIcon, {
      variant: "outlined",
      sx: (theme) => ({
        color: theme.palette.success.main,
        width: 16,
        height: 16,
      }),
    }),
    iconPosition: "right",
    isVisible: isCreditLineItemVisible,
  };
}

function getScoreDisplayProps(params: {
  creditState: CreditState;
  currentStartScore: number;
  currentEndScore: number;
  isScoreAnimating: boolean;
  creditEndScore: number;
  isCreditScoreAnimating: boolean;
}): ScoreDisplayProps {
  const {
    creditState,
    currentStartScore,
    currentEndScore,
    isScoreAnimating,
    creditEndScore,
    isCreditScoreAnimating,
  } = params;

  if (creditState === "idle") {
    return {
      startScore: currentStartScore,
      currentEndScore,
      animationEnabled: isScoreAnimating,
      duration: ANIMATION_TIMINGS.NORMAL_SCORE_ANIMATION_DURATION_MS,
    };
  }

  if (creditState === "applying") {
    return {
      startScore: currentEndScore,
      currentEndScore: creditEndScore,
      animationEnabled: isCreditScoreAnimating,
      duration: ANIMATION_TIMINGS.APPLY_CREDIT_SCORE_ANIMATION_DURATION_MS,
    };
  }

  // "applied" - static final score
  return {
    startScore: creditEndScore,
    currentEndScore: creditEndScore,
    animationEnabled: false,
    duration: ANIMATION_TIMINGS.APPLY_CREDIT_SCORE_ANIMATION_DURATION_MS,
  };
}

function getButtonEnabled(params: {
  isButtonDisabled: boolean;
  creditState: CreditState;
  isButtonEnabled: boolean;
}): boolean {
  const { isButtonDisabled, creditState, isButtonEnabled } = params;

  if (isButtonDisabled || creditState === "applying") {
    return false;
  }

  if (creditState === "applied") {
    return true;
  }

  return isButtonEnabled;
}
